
import { Question } from '@/types/assessment';
import { Badge } from '@/components/ui/badge';
import { CircularButton } from '@/components/ui/circular-button';

interface MaturityQuestionProps {
  question: Question;
  currentValue: string[];
  targetValue: string[];
  onChange: (current: string[], target: string[]) => void;
}

const MaturityQuestion = ({ 
  question, 
  currentValue, 
  targetValue, 
  onChange 
}: MaturityQuestionProps) => {
  const handleCurrentChange = (value: string) => {
    onChange([value], targetValue);
  };
  
  const handleTargetChange = (value: string) => {
    onChange(currentValue, [value]);
  };

  const getMaturityLevelColor = (value: number) => {
    // Create dynamic colors using HSL with increasing saturation/lightness
    const hue = 25; // GapStars orange hue
    const saturation = Math.min(50 + (value * 10), 85); // 50-85%
    const lightness = Math.max(65 - (value * 5), 45); // 65-45%
    return `hsl(${hue} ${saturation}% ${lightness}%)`;
  };

  // Sort answers by level value
  const sortedAnswers = [...question.answers].sort((a, b) => a.value - b.value);

  return (
    <div className="assessment-question-card animate-scale-bounce overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/30 border-b border-border/50">
              <th className="py-3 px-4 text-left font-semibold text-foreground w-16">Level</th>
              <th className="py-3 px-4 text-left font-semibold text-foreground">Description</th>
              <th className="py-3 px-4 text-center font-semibold text-green-700 w-24">Current</th>
              <th className="py-3 px-4 text-center font-semibold text-gapstars-orange w-24">Target</th>
            </tr>
          </thead>
          <tbody>
            {sortedAnswers.map((answer) => {
              const isCurrentSelected = currentValue.includes(answer.id);
              const isTargetSelected = targetValue.includes(answer.id);
              
              return (
                <tr key={answer.id} className="border-b border-border/30 hover:bg-muted/30 assessment-transition">
                  <td className="py-4 px-4 text-center">
                    <div 
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-white text-sm font-bold shadow-sm mx-auto"
                      style={{ backgroundColor: getMaturityLevelColor(answer.value) }}
                    >
                      {answer.value}
                    </div>
                  </td>
                  <td className="py-4 px-4 text-sm text-foreground leading-relaxed">
                    {answer.text}
                  </td>
                  <td className="py-4 px-4 text-center">
                    <CircularButton
                      isSelected={isCurrentSelected}
                      variant="current"
                      onClick={() => handleCurrentChange(answer.id)}
                      size="sm"
                    />
                  </td>
                  <td className="py-4 px-4 text-center">
                    <CircularButton
                      isSelected={isTargetSelected}
                      variant="target"
                      onClick={() => handleTargetChange(answer.id)}
                      size="sm"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MaturityQuestion;
